import React, { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import { onValue, ref, update } from "firebase/database";
import "./liveQuestions.css";
import { fetchThemeData } from "../../themeSlice";
import {
  selectOrganizationId,
  selectSessionId,
} from "../../sessionSlice";
import { getFirebaseDb, isFirebasePickStatsConfigured } from "../../../config/firebase";
import { makePickRoomId } from "../../../utils/pickStatsFirebase";

function toSlugKey(name) {
  return String(name || "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_|_$/g, "");
}

function formatKey(key) {
  return String(key || "")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

function makeQuestionKey(roundKey, index) {
  return `${roundKey}_${index}`;
}

export default function LiveQuestions() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const sessionId = useSelector(selectSessionId);
  const organizationId = useSelector(selectOrganizationId);
  const { currentTheme, data } = useSelector((state) => state.theme);
  const [activeRoundKey, setActiveRoundKey] = useState("");
  const [liveControl, setLiveControl] = useState({
    current: null,
    played: {},
    viewers: {},
    ended: false,
    endedAt: null,
    endedBy: null,
  });
  const [firebaseConnected, setFirebaseConnected] = useState(false);
  const autoEndTriggeredRef = useRef(false);
  const autoEndTimerRef = useRef(null);

  const firebaseReady = isFirebasePickStatsConfigured();
  const database = getFirebaseDb();

  useEffect(() => {
    if (currentTheme) {
      dispatch(fetchThemeData({ themeId: currentTheme }));
    }
  }, [dispatch, currentTheme]);

  const categories = useMemo(() => {
    const cin =
      data?.category_images_names &&
      typeof data.category_images_names === "object" &&
      !Array.isArray(data.category_images_names)
        ? data.category_images_names
        : {};
    const meta =
      data?.category_meta && typeof data.category_meta === "object" && !Array.isArray(data.category_meta)
        ? data.category_meta
        : {};
    const displayCats = Array.isArray(data?.categories) ? data.categories : [];

    const result = [];
    displayCats.forEach((displayName) => {
      const key = toSlugKey(displayName);
      const metaEntry = meta[key] || {};
      result.push({
        key,
        label: metaEntry.label || displayName,
        image: metaEntry.image || "",
        items: Array.isArray(cin[key]) ? cin[key] : [],
      });
    });

    Object.keys(meta).forEach((key) => {
      if (!result.find((entry) => entry.key === key)) {
        const metaEntry = meta[key] || {};
        result.push({
          key,
          label: metaEntry.label || formatKey(key),
          image: metaEntry.image || "",
          items: Array.isArray(cin[key]) ? cin[key] : [],
        });
      }
    });

    Object.keys(cin).forEach((key) => {
      if (!result.find((entry) => entry.key === key)) {
        result.push({
          key,
          label: formatKey(key),
          image: "",
          items: Array.isArray(cin[key]) ? cin[key] : [],
        });
      }
    });

    return result;
  }, [data]);

  useEffect(() => {
    if (!categories.length) {
      setActiveRoundKey("");
      return;
    }
    if (!categories.some((category) => category.key === activeRoundKey)) {
      setActiveRoundKey(categories[0].key);
    }
  }, [categories, activeRoundKey]);

  const pickRoomId = useMemo(
    () => makePickRoomId(sessionId, organizationId),
    [sessionId, organizationId]
  );

  useEffect(() => {
    if (!firebaseReady || !database) return undefined;
    const connectedRef = ref(database, ".info/connected");
    const unsubscribe = onValue(connectedRef, (snap) => {
      setFirebaseConnected(Boolean(snap.val()));
    });
    return () => unsubscribe();
  }, [firebaseReady, database]);

  useEffect(() => {
    if (!firebaseReady || !database || !pickRoomId) return undefined;
    const liveRef = ref(database, `pickRooms/${pickRoomId}/liveControl`);
    const unsubscribe = onValue(liveRef, (snap) => {
      const value = snap.val() || {};
      setLiveControl({
        current: value.current || null,
        played: value.played || {},
        viewers: value.viewers || {},
        ended: value.ended === true,
        endedAt: value.endedAt || null,
        endedBy: value.endedBy || null,
      });
    });
    return () => unsubscribe();
  }, [firebaseReady, database, pickRoomId]);

  const totalQuestions = useMemo(
    () => categories.reduce((sum, round) => sum + round.items.length, 0),
    [categories]
  );

  useEffect(() => {
    autoEndTriggeredRef.current = false;
    if (autoEndTimerRef.current) {
      clearTimeout(autoEndTimerRef.current);
      autoEndTimerRef.current = null;
    }
  }, [pickRoomId]);

  useEffect(() => {
    if (!firebaseReady || !database || !pickRoomId || liveControl.ended === true || totalQuestions === 0) {
      if (autoEndTimerRef.current) {
        clearTimeout(autoEndTimerRef.current);
        autoEndTimerRef.current = null;
      }
      return undefined;
    }

    const played = liveControl.played || {};
    const current = liveControl.current;
    const currentKey = current?.questionKey;

    if (!currentKey) {
      autoEndTriggeredRef.current = false;
      return undefined;
    }

    const allKeys = categories.flatMap((round) =>
      round.items.map((_, index) => makeQuestionKey(round.key, index))
    );

    const othersAllPlayed = allKeys
      .filter((key) => key !== currentKey)
      .every((key) => played[key]);

    if (!othersAllPlayed) {
      autoEndTriggeredRef.current = false;
      if (autoEndTimerRef.current) {
        clearTimeout(autoEndTimerRef.current);
        autoEndTimerRef.current = null;
      }
      return undefined;
    }

    if (autoEndTriggeredRef.current) return undefined;

    autoEndTriggeredRef.current = true;
    autoEndTimerRef.current = setTimeout(async () => {
      try {
        await update(ref(database, `pickRooms/${pickRoomId}/liveControl`), {
          ended: true,
          endedAt: Date.now(),
          endedBy: "all_played",
          current: null,
          [`played/${currentKey}`]: {
            ...current,
            status: "played",
            playedAt: Date.now(),
          },
        });
      } catch (error) {
        autoEndTriggeredRef.current = false;
        console.error("auto end game", error);
      }
    }, 2000);

    return () => {
      if (autoEndTimerRef.current) {
        clearTimeout(autoEndTimerRef.current);
        autoEndTimerRef.current = null;
      }
    };
  }, [
    firebaseReady,
    database,
    pickRoomId,
    liveControl.ended,
    totalQuestions,
    categories,
    liveControl.current,
    liveControl.played,
  ]);

  const activeRound = categories.find((category) => category.key === activeRoundKey) || null;
  const currentQuestion = liveControl.current || null;
  const viewers = Object.values(liveControl.viewers || {});
  const hasEnded = liveControl.ended === true;
  const currentCardTitle = hasEnded
    ? "Game ended"
    : currentQuestion?.roundLabel || "Waiting for selection";
  const currentCardText = hasEnded
    ? "The game has been ended for connected players. You can still push another question live if needed."
    : currentQuestion?.questionText || "No live question is selected yet.";
  const currentBadge = hasEnded ? "Game ended" : currentQuestion ? "Live now" : "Waiting";
  const currentBadgeClass = hasEnded
    ? "is-ended"
    : currentQuestion
      ? "is-live"
      : "is-waiting";

  const getQuestionStatus = (roundKey, index) => {
    const questionKey = makeQuestionKey(roundKey, index);
    if (currentQuestion?.questionKey === questionKey) {
      return { label: "Live now", className: "is-live" };
    }
    if (liveControl.played?.[questionKey]) {
      return { label: "Played", className: "is-played" };
    }
    return { label: "Pending", className: "is-pending" };
  };

  const handleMakeLive = async (round, question, index) => {
    if (!firebaseReady || !database || !pickRoomId) {
      Swal.fire("Firebase not ready", "Check Firebase configuration first.", "warning");
      return;
    }

    const nextQuestionKey = makeQuestionKey(round.key, index);
    const updates = {
      current: {
        roundKey: round.key,
        roundLabel: round.label,
        roundImage: round.image || "",
        questionIndex: index,
        questionKey: nextQuestionKey,
        questionText: question.name || "",
        updatedAt: Date.now(),
      },
      ended: false,
      endedAt: null,
      endedBy: null,
    };

    if (currentQuestion?.questionKey && currentQuestion.questionKey !== nextQuestionKey) {
      updates[`played/${currentQuestion.questionKey}`] = {
        ...currentQuestion,
        status: "played",
        playedAt: Date.now(),
      };
    }

    try {
      await update(ref(database, `pickRooms/${pickRoomId}/liveControl`), updates);
    } catch (error) {
      Swal.fire("Error", error.message || "Failed to update live question.", "error");
    }
  };

  const handleEndGame = async () => {
    if (!firebaseReady || !database || !pickRoomId) {
      Swal.fire("Firebase not ready", "Check Firebase configuration first.", "warning");
      return;
    }

    const result = await Swal.fire({
      title: "End game?",
      text: "This will stop the live game for all connected players.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, end game",
      cancelButtonText: "Cancel",
      confirmButtonColor: "#dc2626",
    });

    if (!result.isConfirmed) return;

    const updates = {
      ended: true,
      endedAt: Date.now(),
      endedBy: "admin",
      current: null,
    };

    if (currentQuestion?.questionKey) {
      updates[`played/${currentQuestion.questionKey}`] = {
        ...currentQuestion,
        status: "played",
        playedAt: Date.now(),
      };
    }

    try {
      await update(ref(database, `pickRooms/${pickRoomId}/liveControl`), updates);
      Swal.fire("Ended", "Game ended for connected viewers.", "success");
    } catch (error) {
      Swal.fire("Error", error.message || "Failed to end the game.", "error");
    }
  };

  return (
    <>
      <div className="back-button-holder">
        <button className="back-button" onClick={() => navigate("/admin/customize-spot")}>
          <i className="fa-solid fa-arrow-left" /> Back
        </button>
      </div>

      <div className="liveq-page">
        <div className="liveq-header">
          <div className="liveq-header-copy">
            <h2 className="liveq-title">
              <i className="fa-solid fa-broadcast-tower" /> &nbsp;Live Question Control
            </h2>
            <p className="liveq-subtitle">
              Select a round and question to push it live to the game page. The current selection
              stays on screen until you choose the next one.
            </p>
          </div>
          <div className="liveq-header-actions">
            <div className="liveq-status-grid">
              <div className="liveq-status-card">
                <span className="liveq-status-label">Firebase</span>
                <strong className={firebaseConnected ? "is-good" : "is-bad"}>
                  {firebaseReady ? (firebaseConnected ? "Connected" : "Disconnected") : "Not configured"}
                </strong>
              </div>
              <div className="liveq-status-card">
                <span className="liveq-status-label">Viewers</span>
                <strong>{viewers.length}</strong>
              </div>
            </div>
            <button type="button" className="liveq-end-btn" onClick={handleEndGame}>
              <i className="fa-solid fa-stop" /> End Game
            </button>
          </div>
        </div>

        {!firebaseReady && (
          <div className="liveq-warning">
            Firebase is not configured. Live control needs the Firebase Realtime Database variables
            in the frontend environment.
          </div>
        )}

        <div className="liveq-current-card">
          <div className="liveq-current-copy">
            <span className={`liveq-current-badge ${currentBadgeClass}`}>{currentBadge}</span>
            <h3>{currentCardTitle}</h3>
            <p>{currentCardText}</p>
          </div>
        </div>

        <div className="liveq-body">
          <div className="liveq-round-tabs">
            {categories.map((round) => (
              <button
                key={round.key}
                type="button"
                className={`liveq-tab${activeRoundKey === round.key ? " is-active" : ""}`}
                onClick={() => setActiveRoundKey(round.key)}
              >
                {round.label}
              </button>
            ))}
          </div>

          {activeRound ? (
            <div className="liveq-table-wrap">
              <div className="liveq-round-head">
                <div>
                  <h3>{activeRound.label}</h3>
                  <div className="liveq-round-meta">{activeRound.items.length} questions</div>
                </div>
              </div>
              <table className="liveq-table">
                <thead>
                  <tr>
                    <th>No.</th>
                    <th>Question</th>
                    <th>Status</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {activeRound.items.map((question, index) => {
                    const status = getQuestionStatus(activeRound.key, index);
                    return (
                      <tr key={`${activeRound.key}-${index}`}>
                        <td>{index + 1}</td>
                        <td className="liveq-question-cell">{question.name}</td>
                        <td>
                          <span className={`liveq-pill ${status.className}`}>{status.label}</span>
                        </td>
                        <td>
                          <button
                            type="button"
                            className="liveq-go-btn"
                            onClick={() => handleMakeLive(activeRound, question, index)}
                          >
                            {status.label === "Live now" ? "Selected" : "Go Live"}
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="liveq-warning">No category questions found for the selected theme.</div>
          )}
        </div>

        {viewers.length > 0 && (
          <div className="liveq-viewers-card">
            <h3>Connected Viewers</h3>
            <div className="liveq-viewers-list">
              {viewers.map((viewer, index) => (
                <div className="liveq-viewer-chip" key={`${viewer.name || "viewer"}-${index}`}>
                  {viewer.name || viewer.userId || "Viewer"}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </>
  );
}
